import { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, ModalDialog, Typography, Button, Slider, Box, Stack } from '@mui/joy';

const VIEWPORT = 260;   // область кадрирования (px)
const OUTPUT   = 512;   // размер итогового квадрата (px)

/**
 * Кадрирование картинки в круг.
 * props: open, src (dataURL), onCancel(), onApply(file: File)
 */
export default function CircleCropModal({ open, src, onCancel, onApply }) {
    const [img, setImg]   = useState(null);            // HTMLImageElement
    const [base, setBase] = useState({ w: 0, h: 0 });  // размер «cover» при zoom=1
    const [zoom, setZoom] = useState(1);
    const [pos, setPos]   = useState({ x: 0, y: 0 });
    const drag = useRef(null);

    useEffect(() => {
        if (!open || !src) return;
        setZoom(1);
        const image = new Image();
        image.onload = () => {
            const scale = Math.max(VIEWPORT / image.width, VIEWPORT / image.height);
            const w = image.width * scale, h = image.height * scale;
            setImg(image);
            setBase({ w, h });
            setPos({ x: (VIEWPORT - w) / 2, y: (VIEWPORT - h) / 2 });
        };
        image.src = src;
    }, [open, src]);

    const clamp = useCallback((p, z) => {
        const dw = base.w * z, dh = base.h * z;
        return {
            x: Math.min(0, Math.max(VIEWPORT - dw, p.x)),
            y: Math.min(0, Math.max(VIEWPORT - dh, p.y)),
        };
    }, [base]);

    const onZoom = (z) => {
        setPos(prev => {
            const c = VIEWPORT / 2;
            const relX = (c - prev.x) / (base.w * zoom);
            const relY = (c - prev.y) / (base.h * zoom);
            return clamp({ x: c - relX * base.w * z, y: c - relY * base.h * z }, z);
        });
        setZoom(z);
    };

    const onPointerDown = (e) => {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        drag.current = { sx: e.clientX, sy: e.clientY, px: pos.x, py: pos.y };
    };
    const onPointerMove = (e) => {
        if (!drag.current) return;
        setPos(clamp({
            x: drag.current.px + (e.clientX - drag.current.sx),
            y: drag.current.py + (e.clientY - drag.current.sy),
        }, zoom));
    };
    const onPointerUp = () => { drag.current = null; };

    const apply = () => {
        if (!img) return;
        const k = OUTPUT / VIEWPORT;
        const canvas = document.createElement('canvas');
        canvas.width = OUTPUT; canvas.height = OUTPUT;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, pos.x * k, pos.y * k, base.w * zoom * k, base.h * zoom * k);
        canvas.toBlob((blob) => {
            if (blob) onApply(new File([blob], 'logo.png', { type: 'image/png' }));
        }, 'image/png', 0.92);
    };

    return (
        <Modal open={open} onClose={onCancel}
            slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.6)' } } }}>
            <ModalDialog sx={{ bgcolor: 'var(--joy-palette-background-level1)', borderRadius: 'var(--joy-radius-xl)', p: 3, maxWidth: 340, width: 'calc(100vw - 32px)', maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto' }}>
                <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 0.3 }}>Кадрируй логотип</Typography>
                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                    Подвигай и приблизь — сохранится то, что в круге.
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Box
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerLeave={onPointerUp}
                        sx={{
                            position: 'relative', width: VIEWPORT, height: VIEWPORT,
                            overflow: 'hidden', borderRadius: 'var(--joy-radius-md)',
                            bgcolor: '#000', cursor: 'grab', touchAction: 'none',
                        }}
                    >
                        {img && (
                            <img src={src} alt="" draggable={false}
                                style={{
                                    position: 'absolute', left: pos.x, top: pos.y,
                                    width: base.w * zoom, height: base.h * zoom,
                                    maxWidth: 'none', userSelect: 'none', pointerEvents: 'none',
                                }} />
                        )}
                        {/* Круглая маска-подсказка */}
                        <Box sx={{
                            position: 'absolute', top: 0, left: 0, width: VIEWPORT, height: VIEWPORT,
                            borderRadius: '50%', pointerEvents: 'none',
                            boxShadow: '0 0 0 9999px rgba(0,0,0,0.45), inset 0 0 0 2px rgba(255,255,255,0.85)',
                        }} />
                    </Box>
                </Box>

                <Slider value={zoom} min={1} max={3} step={0.01} onChange={(_, v) => onZoom(v)} sx={{ mb: 2 }} />

                <Stack direction="row" spacing={1}>
                    <Button variant="plain" color="neutral" onClick={onCancel} sx={{ flex: 1 }}>Отмена</Button>
                    <Button onClick={apply} sx={{ flex: 1, fontWeight: 700 }}>Применить</Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}